import find from 'lodash/find';
import React, { FC, useCallback } from 'react';

import { ConditionOperator, TopicsCondition as TopicsConditionType } from 'src/actions/rules/condition';
import ConditionRow from './ConditionRow';
import {
  StyledDropdown,
  StyledOperatorLabel,
  StyledOperatorDropdown,
} from 'src/presentation/components/pages/rules/shared/StyledComponents';
import useTopics from '../hooks/useTopics';

type TopicsConditionProps = {
  condition: TopicsConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const TopicsCondition: FC<TopicsConditionProps> = ({ condition, index, isReadOnly, onDelete, onUpdate }) => {
  const onOperatorChange = useCallback(
    val => {
      condition.setOperator(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onTopicChange = useCallback(
    topicId => {
      condition.updateTopic(topicId);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const { dataMergeFinished, topicOptions, disabledTopics } = useTopics();
  if (!dataMergeFinished) return null;

  return (
    <ConditionRow index={index} isReadOnly={isReadOnly} onDelete={onDelete} text="Topics include">
      {isReadOnly ? renderReadOnlyCondition() : renderEditableCondition()}
    </ConditionRow>
  );

  function renderEditableCondition() {
    return (
      <>
        <StyledOperatorDropdown
          data-aid="topicsConditionOperatorDropdown"
          onSelect={onOperatorChange}
          options={operatorOptions}
          value={condition.operator}
        />
        <StyledDropdown
          data-aid="topicsConditionValuesDropdown"
          isMultiSelect
          leftMargin
          maxRows={5}
          multiSelectLabel="topics"
          onSelect={onTopicChange}
          options={topicOptions}
          placeholder="Select topics"
          searchable
          value={condition.value.filter(id => !disabledTopics.has(id))}
        />
      </>
    );
  }

  function renderReadOnlyCondition() {
    return (
      <>
        <StyledOperatorLabel data-aid="readOnlyTopicsConditionOperator">
          {renderOperator(condition.operator)}
        </StyledOperatorLabel>
        <span data-aid="readOnlyTopicsConditionValues">{renderTopics(condition.value, topicOptions)}</span>
      </>
    );
  }
};

function renderOperator(value: string) {
  const operator = find(operatorOptions, { value });
  return operator ? operator.label : '';
}

function renderTopics(topics, topicOptions) {
  return topics.map(topic => find(topicOptions, { value: topic }).label).join(', ');
}

const operatorOptions: { label: string; value: string }[] = [
  { label: 'All of', value: ConditionOperator.And },
  { label: 'Any of', value: ConditionOperator.Or },
  { label: 'None of', value: ConditionOperator.Not },
];

export default TopicsCondition;
